import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import Notifier from '../components/ui/Notifier';
import { getErrorMessage } from '../utils/apiError';

/**
 * App-wide feedback for operations.
 *
 * Every screen used to keep its own `show`/`type`/`message` state and render
 * its own <Notifier>, which is how six different notification styles — plus
 * raw alert() — ended up in the codebase. Mount the provider once and call
 * useNotifier() wherever an operation finishes.
 */
const NotificationContext = createContext(null);

const EMPTY = { show: false, type: 'info', message: '' };

export function NotificationProvider({ children, duration = 3000 }) {
  const [state, setState] = useState(EMPTY);

  const hide = useCallback(() => setState(current => ({ ...current, show: false })), []);

  const api = useMemo(() => {
    const show = (type, message) => {
      if (!message) return;
      setState({ show: true, type, message: String(message) });
    };

    return {
      show,
      success: (message) => show('success', message),
      error: (message) => show('error', message),
      warning: (message) => show('warning', message),
      info: (message) => show('info', message),
      /** Report a failed request without repeating the message-unwrapping. */
      fromError: (error, fallback) => show('error', getErrorMessage(error, fallback)),
      dismiss: () => setState(EMPTY),
    };
  }, []);

  return (
    <NotificationContext.Provider value={api}>
      {children}
      <Notifier
        type={state.type}
        message={state.message}
        show={state.show}
        onClose={hide}
        duration={duration}
      />
    </NotificationContext.Provider>
  );
}

export function useNotifier() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifier must be used inside a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
